import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Box, Button, Paper, Stack, Typography, CircularProgress } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import useAuth from "../../hooks/useAuth";
import { ReactComponent as Logo } from "../../vendor/logo.svg";
import colors from "../../utils/colors";
import axios from "../../utils/axios/v1/userAxios";

const Brand = styled(Logo)`
  fill: ${(props) => props.theme.palette.primary.main};
  width: 200px;
  height: 48px;
  margin-bottom: 24px;
`;

const PageWrapper = styled(Box)`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  background: linear-gradient(135deg, ${colors.primary[100]} 0%, ${colors.neutral[50]} 60%, ${colors.neutral[100]} 100%);
`;

const Card = styled(Paper)`
  width: 100%;
  max-width: 480px;
  padding: 40px 32px 36px;
  border-radius: 20px;
  box-shadow: 0px 20px 60px rgba(17, 17, 17, 0.08);
  border: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[100]};
  text-align: center;
`;

const IconBubble = styled(Box)`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px auto;
  background: ${colors.primary[100]};
  border: 1px solid ${colors.primary.alpha || colors.primary[100]};
  box-shadow: 0 8px 24px rgba(99, 113, 247, 0.15);
`;

function VerifyEmail() {
  const [URLSearchParams] = useSearchParams();
  const { resendVerificationEmail, user, signOut } = useAuth();
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const navigate = useNavigate();
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleResendClick = async () => {
    setMessage("");
    setError("");
    setLoading(true);
    try {
      let userId = user?.id;
      if (!userId) {
        const authResponse = await axios.get("/auth");
        userId = authResponse?.data?.id;
      }

      if (!userId) {
        setError("Missing user identifier. Please use the latest verification link.");
        return;
      }

      const res = await resendVerificationEmail(userId);
      if (res?.message === "success" || res?.message === "Verification email is resent") {
        setMessage("Verification email sent successfully. Please check your inbox.");
      } else {
        setError(res?.message || "Unable to resend verification email. Please try again.");
      }
    } catch (err) {
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = async () => {
    setError("");
    setSigningOut(true);
    try {
      if (typeof signOut === "function") {
        await signOut();
      } else {
        navigate("/auth/login", { replace: true });
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err?.response?.data?.message || err?.message || "Unable to sign out right now. Please try again.");
      }
    } finally {
      if (isMountedRef.current) {
        setSigningOut(false);
      }
    }
  };

  return (
    <PageWrapper>
      <Helmet title="Verify Email" />
      <Stack
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: 480,
          py: { xs: 1, sm: 2 },
          transform: { xs: "translateY(-20px)", sm: "translateY(-56px)" },
        }}
      >
        <Brand />
        <Card elevation={0}>
          <IconBubble>
            <MailOutlineIcon sx={{ fontSize: 44, color: colors.primary[300] }} />
          </IconBubble>

          <Typography
            sx={{
              fontSize: { xs: 28, sm: 32 },
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: colors.neutral[1000],
              lineHeight: 1.2,
              mb: 1.5,
            }}
          >
            Verify your email
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: 15, sm: 16 },
              fontWeight: 400,
              color: colors.neutral[600],
              lineHeight: 1.65,
              mb: 4,
              px: { xs: 0.5, sm: 2 },
            }}
          >
            We&apos;ve sent a verification link to your email. Please click the link to confirm your address and
            continue.
          </Typography>

          {message && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{
                mb: 3,
                px: 2,
                py: 1.25,
                borderRadius: 2,
                backgroundColor: colors.green[10],
                border: `1px solid ${colors.green[200]}`,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 18, color: colors.green[300] }} />
              <Typography sx={ { fontSize: 13, color: colors.green[300] }}>{message}</Typography>
            </Stack>
          )}

          {error && (
            <Box
              sx={{
                mb: 3,
                px: 2,
                py: 1.25,
                borderRadius: 2,
                backgroundColor: colors.red[10],
                border: `1px solid ${colors.red[100]}`,
              }}
            >
              <Typography sx={ { fontSize: 13, color: colors.red[200] }}>{error}</Typography>
            </Box>
          )}

          <Box
            sx={{
              borderTop: `1px dashed ${colors.neutral[200]}`,
              pt: 2.5,
              mt: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 400,
                color: colors.neutral[600],
                lineHeight: 1.55,
                mb: 2.25,
              }}
            >
              Didn&apos;t receive the email? Check your spam folder or resend below.
            </Typography>
            <Button
              variant="contained"
              onClick={handleResendClick}
              disabled={loading || signingOut}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 15,
                color: colors.neutral[100],
                minWidth: 260,
                minHeight: 48,
                px: 3.5,
                borderRadius: 2.5,
                boxShadow: "none",
                backgroundColor: colors.primary[300],
                border: `1px solid ${colors.primary[400]}`,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: colors.primary[400],
                  color: colors.neutral[100],
                  borderColor: colors.primary[500],
                  boxShadow: "0 8px 18px rgba(99, 113, 247, 0.28)",
                  transform: "translateY(-1px)",
                },
                "&.Mui-disabled": {
                  backgroundColor: colors.neutral[300],
                  color: colors.neutral[100],
                  borderColor: colors.neutral[300],
                },
              }}
            >
              {loading ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={16} sx={{ color: colors.neutral[100] }} />
                  <span>Resending...</span>
                </Stack>
              ) : (
                "Resend verification email"
              )}
            </Button>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center" sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleBackToLogin}
                disabled={loading || signingOut}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 15,
                  minWidth: 260,
                  minHeight: 48,
                  px: 3.5,
                  borderRadius: 2.5,
                  borderWidth: 1,
                  borderColor: colors.neutral[300],
                  color: colors.neutral[700],
                  "&:hover": {
                    borderWidth: 1,
                    borderColor: colors.primary[300],
                    color: colors.primary[400],
                    backgroundColor: colors.primary[10],
                  },
                }}
              >
                {signingOut ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={14} />
                    <span>Redirecting...</span>
                  </Stack>
                ) : (
                  "Back to login"
                )}
              </Button>
            </Stack>
          </Box>
        </Card>

        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 400,
            color: colors.neutral[500],
            textAlign: "center",
            mt: 2.5,
          }}
        >
          Need help? Contact support if you continue to have issues.
        </Typography>
      </Stack>
    </PageWrapper>
  );
}

export default VerifyEmail;
